import type { CalendarEvent, CalendarEventStats } from "../types";

// Helper to generate dates
function generateUpcomingDates(daysAhead: number, count: number): Date[] {
  const dates: Date[] = [];
  const now = new Date();

  for (let i = 0; i < count; i++) {
    const date = new Date(now);
    date.setDate(date.getDate() + Math.floor(Math.random() * daysAhead) + 1);
    // Set to business hours (9 AM - 5 PM)
    date.setHours(
      9 + Math.floor(Math.random() * 8),
      Math.floor(Math.random() * 60),
      0,
      0,
    );
    dates.push(date);
  }

  return dates.sort((a, b) => a.getTime() - b.getTime());
}

// Helper to generate events by day for the last 30 days
function generateEventsByDay(): {
  date: string;
  count: number;
  value: number;
}[] {
  const days: { date: string; count: number; value: number }[] = [];
  const today = new Date();

  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    const dateStr = date.toISOString().split("T")[0];

    // More events on weekdays
    const isWeekday = date.getDay() >= 1 && date.getDay() <= 5;
    const baseCount = isWeekday ? 8 : 2;
    const variance = Math.floor(Math.random() * 4) - 2;
    const count = Math.max(0, baseCount + variance);

    days.push({
      date: dateStr,
      count,
      value: count * 4500, // Average $45 per event
    });
  }

  return days;
}

// Generate mock events
function generateMockEvents(
  count: number,
  daysAhead: number = 30,
): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  const dates = generateUpcomingDates(daysAhead, count);

  const summaries = [
    "Consultation - Sarah Johnson",
    "Appointment - Michael Smith",
    "Meeting - Emma Williams",
    "Session - James Brown",
    "Consultation - Olivia Davis",
    "Appointment - William Miller",
    "Meeting - Sophia Wilson",
    "Session - Robert Moore",
  ];

  const locations = [
    "Main Office",
    "Conference Room 2",
    "Consultation Suite",
    "Office 1",
  ];

  const serviceIds = [
    "service_consultation",
    "service_appointment",
    "service_meeting",
    "service_session",
  ];

  for (let i = 0; i < count; i++) {
    const startTime = dates[i % dates.length].getTime();
    const duration = [30, 45, 60, 90][Math.floor(Math.random() * 4)]; // minutes
    const endTime = startTime + duration * 60 * 1000;
    const summary = summaries[i % summaries.length];
    const customerName = summary.split(" - ")[1];

    events.push({
      id: `event_${i + 1}_${startTime}`,
      client_id: "demo_client",
      calendar_id: "calendar_1",
      summary,
      description: `${duration} minute ${serviceIds[i % serviceIds.length].replace("service_", "")} with ${customerName}`,
      location: locations[i % locations.length],
      start_time: startTime,
      end_time: endTime,
      is_all_day: false,
      timezone: "America/New_York",
      is_recurring: false,
      status: "confirmed",
      attendees: {
        attendees: [
          {
            email: `${customerName.toLowerCase().replace(" ", ".")}@example.com`,
            name: customerName,
            response_status: "accepted",
            is_organizer: false,
          },
        ],
      },
      service_id: serviceIds[i % serviceIds.length],
      customer_id: `customer_${(i % 50) + 1}`,
      performer_ids: ["performer_1"],
      is_primary: true,
      sync_status: "synced",
      created_at: new Date(startTime - 7 * 24 * 60 * 60 * 1000).toISOString(),
      updated_at: new Date(startTime - 7 * 24 * 60 * 60 * 1000).toISOString(),
    });
  }

  return events;
}

// Mock data for useEvents
export const mockEvents: CalendarEvent[] = generateMockEvents(50, 30);

// Mock data for useCalendarStats
export const mockCalendarEventStats: CalendarEventStats = {
  upcoming_events: {
    next_7_days: 42,
    next_14_days: 78,
    next_30_days: 145,
  },
  upcoming_value: {
    next_7_days: 18900, // 42 events * $45 average
    next_14_days: 35100, // 78 events * $45 average
    next_30_days: 65250, // 145 events * $45 average
  },
  events_by_period: {
    last_month: 138,
    this_month: 152,
  },
  value_by_period: {
    last_month: 62100, // 138 * $45
    this_month: 68400, // 152 * $45
  },
  events_by_day: generateEventsByDay(),
  events_by_service: [
    {
      service_id: "service_consultation",
      service_name: "Initial Consultation",
      count: 85,
      value: 38250, // 85 * $45
    },
    {
      service_id: "service_appointment",
      service_name: "Standard Appointment",
      count: 120,
      value: 54000, // 120 * $45
    },
    {
      service_id: "service_meeting",
      service_name: "Extended Meeting",
      count: 45,
      value: 20250, // 45 * $45
    },
    {
      service_id: "service_session",
      service_name: "Complete Session",
      count: 40,
      value: 18000, // 40 * $45
    },
  ],
};
